// Fouten begrijpelijk tonen: wat er misging en wat je kunt doen. Nooit stacktraces of SQL.
// Servermeldingen zijn Nederlands; een bekende foutcode krijgt daarom altijd een tekst uit de catalogus
// (sleutel error.code.<code>), zodat iemand met een Engelse interface geen Nederlandse melding ziet.
import type { ReactNode } from "react";
import { ArrowClockwiseIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { Button, EmptyState, Notice, toast } from "@bres/ui";
import { ApiError } from "./api.ts";
import type { Translate } from "./i18n.tsx";
import { useT } from "./i18n.tsx";

/** Foutcodes met een eigen tekst in de catalogus (common.json, error.code.<code>). */
const CODE_TEXT = new Set([
  "version_conflict",
  "ai.not_in_plan",
  "blog.checks_blocking",
  "blog.test_output",
  "blog.locale_locked",
  "blog.archived",
  "blog.not_draft",
  "blog.not_in_review",
  "blog.locale_published",
  "blog.slug_taken",
  "chat.unavailable",
  "localization.test_output",
  "localization.publish_incomplete",
  "localization.base_mismatch",
  "localization.source_changed",
  "localization.qa_critical",
  "localization.not_reviewable",
  "localization.use_source_editor",
  "ai.budget_exceeded",
  "ai.unavailable",
  "ai.unknown_locale",
  "ai.unsupported_owner",
  "ai.refused",
  "import.source_paused",
  "import.upload_required",
  "import.not_awaiting_review",
  "import.source_changed",
  "import.mass_withdrawal_unconfirmed",
  "channel.kill_switch",
  "channel.no_feed_adapter",
  "channel.no_publication",
  "channel.status_requires_evidence",
  "media.order_mismatch",
  "media.cover_must_be_photo",
  "restore.no_active_offer",
  "publication.no_locales",
  "build.generation_mismatch",
  "build.release_missing",
]);

/** Foutcode van de API, of null bij een netwerk- of programmafout. */
export function errorCode(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}

export function errorText(t: Translate, error: unknown): string {
  if (!(error instanceof ApiError)) return t("error.server");
  if (error.status === 0) return t("error.network");
  if (CODE_TEXT.has(error.code)) return t(`error.code.${error.code}`);
  if (error.status === 403) return t("error.forbidden");
  if (error.status === 404) return t("error.not_found");
  if (error.status === 409) return t("error.conflict");
  if (error.status === 422) {
    if (error.fieldErrors.reference?.includes("reference_taken")) return t("error.field.reference_taken");
    return error.message && error.code !== "validation_failed" ? error.message : t("error.validation");
  }
  if (error.status === 429) return t("error.rate_limited");
  if (error.status >= 500) return t("error.server");
  return error.message || t("error.server");
}

/**
 * Melding na een mislukte actie; met `retry` krijgt de melding een knop om het opnieuw te proberen.
 * Werkt ook direct als `onError` van React Query: het tweede argument is dan de mutatievariabele en wordt genegeerd.
 */
export function useErrorToast(): (error: unknown, retry?: unknown) => void {
  const t = useT();
  return (error, retry) =>
    toast({
      title: t("error.title"),
      description: errorText(t, error),
      tone: "danger",
      action: typeof retry === "function" ? { label: t("common.retry"), onAction: retry as () => void } : undefined,
    });
}

export function InlineError({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const t = useT();
  const id = error instanceof ApiError ? error.requestId : null;
  return (
    <Notice
      tone="danger"
      role="alert"
      title={t("error.title")}
      actions={
        onRetry ? (
          <Button size="sm" variant="secondary" icon={<ArrowClockwiseIcon aria-hidden />} onPress={onRetry}>
            {t("common.retry")}
          </Button>
        ) : undefined
      }
    >
      {errorText(t, error)}
      {id ? <span className="subtle"> {t("error.reference", { id })}</span> : null}
    </Notice>
  );
}

export function PageError({ error, onRetry, children }: { error: unknown; onRetry?: () => void; children?: ReactNode }) {
  const t = useT();
  return (
    <EmptyState
      icon={<WarningCircleIcon />}
      title={t("error.page_title")}
      actions={
        onRetry ? (
          <Button variant="secondary" icon={<ArrowClockwiseIcon aria-hidden />} onPress={onRetry}>
            {t("common.retry")}
          </Button>
        ) : undefined
      }
    >
      {errorText(t, error)}
      {children}
    </EmptyState>
  );
}

/** Veldfouten van de server op veldnaam. */
export function fieldError(error: unknown, field: string, t?: Translate): string | undefined {
  return error instanceof ApiError ? error.fieldErrors[field]?.map((message) => t && message === "reference_taken" ? t("error.field.reference_taken") : message).join(" ") : undefined;
}
